import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetCustomersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/get-customers-paginated.model';
import { GatewayCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/outputs/gateway-customer.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerIsExemptFromBillingFilterStateController } from '../customer-is-exempt-from-billing-filter-state/customer-is-exempt-from-billing-filter-state.controller';
import { CustomerListHolderCompanyNameColumn } from './customer-list-holder-company-name.column';
import { CustomerListHolderEmailColumn } from './customer-list-holder-email.column';
import { CustomerListHolderFullNameColumn } from './customer-list-holder-full-name.column';
import { CustomerListIdColumn } from './customer-list-id.column';
import { CustomerListNameColumn } from './customer-list-name.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const CUSTOMER_LIST_SORT_FIELD: NonNullable<
  GetCustomersPaginatedModel['sortField']
> = 'createdAt';

export const CUSTOMER_LIST_SORT_ORDER: NonNullable<
  GetCustomersPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetCustomersPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const CUSTOMER_LIST_FILTER: ResourceListFilter = {
  isExemptFromBilling: {
    componentType: CustomerIsExemptFromBillingFilterStateController,
  },
} as const;

export const CUSTOMER_LIST_FILTER_NAME_LIST = Object.keys(
  CUSTOMER_LIST_FILTER,
) as Array<keyof typeof CUSTOMER_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const customerListColumnHelper =
  createColumnHelper<GatewayCustomerModel>();

type ResourceColumnType = ColumnDef<GatewayCustomerModel, any>[];

export const CUSTOMER_LIST_COLUMN_LIST: ResourceColumnType = [
  customerListColumnHelper.accessor('id', CustomerListIdColumn),
  customerListColumnHelper.accessor('name', CustomerListNameColumn),
  customerListColumnHelper.accessor(
    'holder.companyName',
    CustomerListHolderCompanyNameColumn,
  ),
  customerListColumnHelper.accessor('holder', CustomerListHolderFullNameColumn),
  customerListColumnHelper.accessor(
    'holder.email',
    CustomerListHolderEmailColumn,
  ),
];

export const CUSTOMER_LIST_COLUMN_ID_LIST = CUSTOMER_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const CUSTOMER_LIST_COLUMN_ORDER = [...CUSTOMER_LIST_COLUMN_ID_LIST];

export const CUSTOMER_LIST_COLUMN_VISIBILITY =
  CUSTOMER_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {});

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface CustomerListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const CUSTOMER_LIST_DEFAULT_OPTIONS: CustomerListOptionsModel = {
  columnVisibility: CUSTOMER_LIST_COLUMN_VISIBILITY,
  columnOrder: CUSTOMER_LIST_COLUMN_ORDER,
};

export const CUSTOMER_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  id: 'delmonicos-shared-sdk.outputs-base.id',
  name: 'delmonicos-shared-sdk.outputs-customer.name',
  holderCompanyName: 'delmonicos-shared-sdk.outputs-holder.company-name',
  holder: 'delmonicos-shared-sdk.outputs-customer.holder',
  holderEmail: 'delmonicos-shared-sdk.outputs-holder.email',
};
